import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export default function Empresas() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    nombre: "",
    rut: "",
    giro: "",
    direccion: "",
  });
  const { refreshUser } = useOutletContext() || {};
  const { refresh } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/empresas");
      setEmpresas(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/empresas", { ...form, moneda: "CLP", pais: "CL" });
      toast.success("Empresa creada");
      setOpen(false);
      setForm({ nombre: "", rut: "", giro: "", direccion: "" });
      load();
      (refreshUser || refresh)?.();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <Page>
      <PageHeader
        title="Empresas"
        subtitle="Administra tus empresas (entidades contables)"
        testid="empresas-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="new-empresa-button"
                className="inline-flex items-center gap-1.5 bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-3 py-2 rounded-sm"
              >
                <Plus className="h-4 w-4" /> Nueva empresa
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nueva empresa</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="new-empresa-form">
                <div>
                  <label className="label-caps block mb-1">Razón social</label>
                  <input
                    required
                    data-testid="empresa-nombre"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">RUT</label>
                  <input
                    required
                    data-testid="empresa-rut"
                    value={form.rut}
                    onChange={(e) => setForm({ ...form, rut: e.target.value })}
                    placeholder="76.123.456-7"
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Giro</label>
                  <input
                    data-testid="empresa-giro"
                    value={form.giro}
                    onChange={(e) => setForm({ ...form, giro: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Dirección</label>
                  <input
                    data-testid="empresa-direccion"
                    value={form.direccion}
                    onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                  />
                </div>
                <DialogFooter>
                  <button
                    type="submit"
                    data-testid="submit-empresa"
                    className="bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-4 py-2 rounded-sm"
                  >
                    Crear empresa
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-[#E2DFD8] rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[#59645E] text-sm">Cargando…</div>
        ) : empresas.length === 0 ? (
          <div className="p-8 text-center text-[#59645E] text-sm">No tienes empresas aún.</div>
        ) : (
          <table className="acc-table" data-testid="empresas-table">
            <thead>
              <tr>
                <th>Razón social</th>
                <th style={{ width: 160 }}>RUT</th>
                <th>Giro</th>
                <th>Dirección</th>
                <th style={{ width: 80 }}>Moneda</th>
                <th style={{ width: 110 }}>Creada</th>
              </tr>
            </thead>
            <tbody>
              {empresas.map((e) => (
                <tr key={e.id} data-testid={`empresa-row-${e.id}`}>
                  <td className="font-medium">{e.nombre}</td>
                  <td className="font-mono">{e.rut}</td>
                  <td>{e.giro || "—"}</td>
                  <td>{e.direccion || "—"}</td>
                  <td>{e.moneda}</td>
                  <td>{fmtDate(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Page>
  );
}
